import React, { useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

import useProductListingData from 'hooks/useProductListingData';
import ProductListingPageContext from 'data-hooks/context/ProductListingPageContext';
import { GET_CUSTOM_LIST_PRODUCT_DATA_V2 } from 'mocks/queries';
import AdminPlaceholder from 'components/consumer/AdminPlaceholder';
import CustomProductListUI from './CustomProductListUI';

const CustomProductListInner = ({ config, isAdmin = false }) => {
  const hasLoadingAllDataStartedRef = useRef(false);
  const {
    listStyle,
    toggleSourceType,
    sortByPopularity = false,
    filterByStock = false,
    listCardSize: { cardSizeSelected = 'medium' } = {},
  } = config || {};
  const { productList = [], customListSlug: slug = '', selected } =
    toggleSourceType || {};

  const productListWithSelectedOptions = useMemo(
    () =>
      productList.map(({ id, category, isInactive, selectedOptions }) => ({
        id,
        category,
        isInactive,
        selectedOptions: {
          optionId: selectedOptions?.optionId || null,
          optionType: selectedOptions?.optionType || '',
          optionValue: selectedOptions?.optionValue || '',
          optionValueId: selectedOptions?.optionValueId || null,
          optionValueSku: selectedOptions?.optionValueSku || '',
        },
      })),
    [productList]
  );

  const { loading, loadAllProducts, ...data } = useProductListingData(
    GET_CUSTOM_LIST_PRODUCT_DATA_V2,
    {
      variables: {
        filterByStock,
        sortByPopularity,
        ...(selected?.value === 'ProductsListInput'
          ? {
              productList: productListWithSelectedOptions,
            }
          : { slug }),
      },
      skip: (!slug && !productList?.length) || isAdmin,
    }
  );

  const dataWithState = useMemo(
    () => ({
      ...data,
      loading,
      loadAllProducts,
    }),
    [data, loading, loadAllProducts]
  );

  useEffect(() => {
    // If loading of all products has not yet started, load all products when
    // the browser is idle
    if (!loading && !hasLoadingAllDataStartedRef.current && loadAllProducts) {
      hasLoadingAllDataStartedRef.current = true;
      window.requestIdleCallback(async () => {
        loadAllProducts();
      });
    }
  }, [loading, loadAllProducts]);

  return (
    <ProductListingPageContext.Provider value={dataWithState}>
      <CustomProductListUI listStyle={listStyle} cardSize={cardSizeSelected} />
    </ProductListingPageContext.Provider>
  );
};

const CustomProductList = props => {
  if (props?.isAdmin) {
    return (
      <AdminPlaceholder className="!min-h-[470px]">
        Custom Product List Admin Placeholder
      </AdminPlaceholder>
    );
  }
  if (props?.config?.elementAttribute?.id) {
    return (
      <div id={props?.config?.elementAttribute?.id}>
        <CustomProductListInner {...props} />
      </div>
    );
  }
  return <CustomProductListInner {...props} />;
};

CustomProductList.propTypes = {
  isAdmin: PropTypes.bool,
  config: PropTypes.shape({
    elementAttribute: PropTypes.shape({
      id: PropTypes.string,
    }),
  }),
};

CustomProductListInner.propTypes = {
  config: PropTypes.object,
  isAdmin: PropTypes.bool,
};

export default CustomProductList;
